"use client";

import {
  IconBook,
  IconBox,
  IconBrandSpeedtest,
  IconCircleCheck,
  IconCircleX,
  IconExternalLink,
  IconFolder,
  IconHome,
  IconLogin2,
  IconSettings,
  IconShield,
  IconTruck,
} from "@tabler/icons-react";
import { Badge } from "@/components/ui/badge";
import { DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";
import React, { useState } from "react";
import { useTranslations } from "../../../../helpers/translations";
import { useUserContext } from "../../../../providers/UserProvider";
import { RAILS_ROUTES } from "../../helpers/routes";
import { DropdownMenuItemIcon } from "./DropdownMenuItemIcon";
import { DropdownMenuItemLabel } from "./DropdownMenuItemLabel";
import { DropdownMenuItemLink } from "./DropdownMenuItemLink";

export const MainMenuContent = () => {
  const { currentUserAll, toggleUserAdmin, logoutUser } = useUserContext();

  const user = currentUserAll.data?.me;
  const isLoggedIn = !!user;
  const hasOrganization = !!user?.organization?.id;

  const [adminOn, setAdminOn] = useState<boolean>(user?.adminOn || false);

  const t = useTranslations("nav.userMenu");

  return (
    <>
      {isLoggedIn && (
        <>
          <DropdownMenuItemLink href={RAILS_ROUTES.DASHBOARD}>
            <DropdownMenuItemIcon icon={IconHome} size={16} />
            <DropdownMenuItemLabel>{t("dashboard")}</DropdownMenuItemLabel>
          </DropdownMenuItemLink>

          <DropdownMenuItemLink href={RAILS_ROUTES.DASHBOARD_PROJECTS}>
            <DropdownMenuItemIcon icon={IconFolder} size={16} />
            <DropdownMenuItemLabel>{t("projects")}</DropdownMenuItemLabel>
            <Badge variant="brand" size="sm">Beta</Badge>
          </DropdownMenuItemLink>

          <DropdownMenuItemLink href={RAILS_ROUTES.DASHBOARD_DESIGNS}>
            <DropdownMenuItemIcon icon={IconBox} size={16} />
            <DropdownMenuItemLabel>{t("designs")}</DropdownMenuItemLabel>
          </DropdownMenuItemLink>

          {hasOrganization && (
            <DropdownMenuItemLink href={RAILS_ROUTES.DASHBOARD_CATALOG}>
              <DropdownMenuItemIcon icon={IconBook} size={16} />
              <DropdownMenuItemLabel>{t("catalog")}</DropdownMenuItemLabel>
            </DropdownMenuItemLink>
          )}

          <DropdownMenuItemLink href={RAILS_ROUTES.DASHBOARD_ORDERS}>
            <DropdownMenuItemIcon icon={IconTruck} size={16} />
            <DropdownMenuItemLabel>{t("orders")}</DropdownMenuItemLabel>
          </DropdownMenuItemLink>

          <DropdownMenuItemLink href={RAILS_ROUTES.DASHBOARD_MACHINE_ANALYTICS}>
            <DropdownMenuItemIcon icon={IconBrandSpeedtest} size={16} />
            <DropdownMenuItemLabel>{t("analytics")}</DropdownMenuItemLabel>
          </DropdownMenuItemLink>

          <DropdownMenuSeparator />

          <DropdownMenuItemLink href={RAILS_ROUTES.DASHBOARD_SETTINGS_USER}>
            <DropdownMenuItemIcon icon={IconSettings} size={16} />
            <DropdownMenuItemLabel>{t("settings")}</DropdownMenuItemLabel>
          </DropdownMenuItemLink>
        </>
      )}

      {user?.canAdmin && (
        <>
          <DropdownMenuSeparator />
          <DropdownMenuItemLink
            onClick={(event) => {
              event.preventDefault();
              toggleUserAdmin.mutate();
              setAdminOn((currentAdminModeOn) => !currentAdminModeOn);
            }}
            href="#"
          >
            <DropdownMenuItemIcon icon={IconShield} size={16} />
            <DropdownMenuItemLabel>
              {t("admin.toggle")}{" "}
              {adminOn ? (
                <IconCircleCheck className="ml-sm text-success-500" />
              ) : (
                <IconCircleX className="ml-sm text-error-500" />
              )}
            </DropdownMenuItemLabel>
          </DropdownMenuItemLink>

          {adminOn && (
            <DropdownMenuItemLink href={RAILS_ROUTES.ADMIN} target="_blank">
              <DropdownMenuItemIcon icon={IconExternalLink} size={16} />
              <DropdownMenuItemLabel>
                {t("admin.dashboard")}
              </DropdownMenuItemLabel>
            </DropdownMenuItemLink>
          )}

          {adminOn && (
            <DropdownMenuItemLink
              href={RAILS_ROUTES.ADMIN_CHECKOUT}
              target="_blank"
            >
              <DropdownMenuItemIcon icon={IconExternalLink} size={16} />
              <DropdownMenuItemLabel>
                {t("admin.checkout")}
              </DropdownMenuItemLabel>
            </DropdownMenuItemLink>
          )}
        </>
      )}

      {isLoggedIn && <DropdownMenuSeparator />}

      {isLoggedIn && (
        <DropdownMenuItemLink
          className={cn({
            "opacity-50 pointer-events-none": logoutUser.isPending,
          })}
          onClick={(event) => {
            event.preventDefault();
            logoutUser.mutate(
              {},
              {
                onSuccess: () => {
                  window.location.reload();
                },
              },
            );
          }}
        >
          <DropdownMenuItemIcon icon={IconLogin2} size={16} />
          <DropdownMenuItemLabel>{t("logout")}</DropdownMenuItemLabel>
        </DropdownMenuItemLink>
      )}

      {!isLoggedIn && (
        <DropdownMenuItemLink href={RAILS_ROUTES.USERS_SIGN_IN}>
          <DropdownMenuItemIcon icon={IconLogin2} size={16} />
          <DropdownMenuItemLabel>{t("login")}</DropdownMenuItemLabel>
        </DropdownMenuItemLink>
      )}
    </>
  );
};
